/**
 * Shared editor dialog management for sampler library pages.
 *
 * Provides the full complement of common-area editing tools:
 * - Sample Editor (trim, normalize, fade, reverse)
 * - Loop Editor (edit loop points)
 * - Sample Chopper (slice samples into drum kits)
 * - Drum Kit Editor (edit kit metadata and per-pad config)
 * - Slice Edit Dialog (edit existing drum kit slices)
 *
 * Device-agnostic -- loads WAV data from the common area by default.
 * Device-specific loading is handled by the EditorDialogStrategy
 * (e.g., loading Roland tones or S3K program samples).
 *
 * Editing a device-specific object saves to the common area (Zone 4).
 * This is an implicit promotion -- editors produce vendor-agnostic output.
 */
import { useState, useCallback } from 'react';
import { stringify as stringifyYaml } from 'yaml';
import { loadSample, loadSampleMeta, loadProgram, loadProgramMeta, saveSample, saveProgram, parseWav, createWav, getNestedDirectory, sanitizeForFilename, } from '@audiocontrol/sampler-library/browser';
// =========================================================================
// Default (empty) strategy
// =========================================================================
const EMPTY_STRATEGY = {
    loadWav: async () => null,
};
// =========================================================================
// Shared hook
// =========================================================================
export function useEditorDialogsCore(libraryRoot, strategy = EMPTY_STRATEGY, onRefresh, errorReporter) {
    const onError = errorReporter.report;
    const [loopEditor, setLoopEditor] = useState(null);
    const [sampleEditor, setSampleEditor] = useState(null);
    const [chopper, setChopper] = useState(null);
    const [sliceEditDialog, setSliceEditDialog] = useState(null);
    const [drumKitEditor, setDrumKitEditor] = useState(null);
    // ---------------------------------------------------------------------------
    // WAV loading: strategy first, common-area fallback
    // ---------------------------------------------------------------------------
    const loadWavData = useCallback(async (name, nodeType, path) => {
        // Try device-specific strategy first — strategy may not need libraryRoot
        // (e.g., loading device samples via SDS bypasses library entirely)
        const strategyResult = await strategy.loadWav(libraryRoot, name, nodeType, path);
        if (strategyResult)
            return strategyResult;
        // Common-area loading requires a library root
        if (!libraryRoot)
            throw new Error('Library not connected');
        // Common-area samples: sample.yaml + sample.wav
        if (nodeType === 'sample') {
            const result = await loadSample(libraryRoot, name, path);
            const wav = parseWav(result.wavData);
            return {
                samples: wav.samples,
                sampleRate: wav.sampleRate,
                loopStart: result.yaml.loopStart ?? undefined,
                loopEnd: result.yaml.loopEnd ?? undefined,
                rootKey: typeof result.yaml.rootKey === 'number' ? result.yaml.rootKey : undefined,
            };
        }
        // Common-area programs: program.yaml + samples/*.wav
        if (nodeType === 'program') {
            const programResult = await loadProgram(libraryRoot, name);
            if (programResult.wavFiles.length === 0) {
                throw new Error(`Program "${name}" has no WAV files`);
            }
            const firstWav = programResult.wavFiles[0];
            const wav = parseWav(firstWav.data);
            return {
                samples: wav.samples,
                sampleRate: wav.sampleRate,
            };
        }
        throw new Error(`Unsupported node type: ${nodeType}`);
    }, [libraryRoot, strategy]);
    // ---------------------------------------------------------------------------
    // Loop editor
    // ---------------------------------------------------------------------------
    const handleOpenInLoopEditor = useCallback(async (name, nodeType, path) => {
        try {
            const wav = await loadWavData(name, nodeType, path);
            setLoopEditor({
                open: true, samples: wav.samples, sampleRate: wav.sampleRate,
                sampleName: name, loopStart: wav.loopStart, loopEnd: wav.loopEnd,
                rootKey: wav.rootKey,
                origin: { name, type: nodeType, path },
            });
        }
        catch (err) {
            onError(err instanceof Error ? err.message : 'Failed to load sample');
        }
    }, [loadWavData, onError]);
    const handleLoopEditorSave = useCallback(async (loopStart, loopEnd) => {
        if (!loopEditor?.origin || !libraryRoot)
            return;
        const { name, path } = loopEditor.origin;
        try {
            const yaml = await loadSampleMeta(libraryRoot, name, path ?? []);
            yaml.loopStart = loopStart;
            yaml.loopEnd = loopEnd;
            yaml.loopMode = 'forward';
            yaml.modifiedAt = new Date().toISOString();
            const fullPath = ['library', 'common', 'samples', ...(path ?? [])];
            const safeName = sanitizeForFilename(name);
            const samplesDir = await getNestedDirectory(libraryRoot, fullPath);
            const sampleDir = await samplesDir.getDirectoryHandle(safeName);
            const yamlHandle = await sampleDir.getFileHandle('sample.yaml', { create: true });
            const writable = await yamlHandle.createWritable();
            await writable.write(stringifyYaml(yaml, { indent: 2, lineWidth: 120 }));
            await writable.close();
        }
        catch (err) {
            onError(err instanceof Error ? err.message : 'Failed to save loop points');
        }
    }, [loopEditor, libraryRoot, onError]);
    // ---------------------------------------------------------------------------
    // Sample editor
    // ---------------------------------------------------------------------------
    const handleOpenInSampleEditor = useCallback(async (name, nodeType, path) => {
        try {
            const wav = await loadWavData(name, nodeType, path);
            setSampleEditor({
                open: true, samples: wav.samples, sampleRate: wav.sampleRate,
                sampleName: name,
                origin: { name, type: nodeType, path },
            });
        }
        catch (err) {
            onError(err instanceof Error ? err.message : 'Failed to load sample');
        }
    }, [loadWavData, onError]);
    const handleSampleEditorSave = useCallback(async (samples, sampleRate) => {
        if (!sampleEditor?.origin || !libraryRoot)
            return;
        const { name, path } = sampleEditor.origin;
        try {
            const existingMeta = await loadSampleMeta(libraryRoot, name, path ?? []);
            existingMeta.sampleRate = sampleRate;
            existingMeta.modifiedAt = new Date().toISOString();
            const wavData = createWav(samples, sampleRate);
            await saveSample(libraryRoot, { name, yaml: existingMeta, wavData }, path ?? []);
            onRefresh();
        }
        catch (err) {
            onError(err instanceof Error ? err.message : 'Failed to save edited sample');
        }
    }, [sampleEditor, libraryRoot, onRefresh, onError]);
    // ---------------------------------------------------------------------------
    // Sample chopper
    // ---------------------------------------------------------------------------
    const handleOpenInChopper = useCallback(async (name, nodeType, path) => {
        try {
            const wav = await loadWavData(name, nodeType, path);
            let initialSlices;
            let initialLabels;
            let chopperSampleName = name;
            if (libraryRoot && nodeType === 'sample') {
                // Load existing slice definitions from sample metadata (if available)
                try {
                    const meta = await loadSampleMeta(libraryRoot, name, path);
                    if (meta.slices && meta.slices.length > 0) {
                        initialSlices = meta.slices.map((s) => ({
                            label: s.label, startSample: s.startSample, endSample: s.endSample,
                        }));
                        initialLabels = meta.slices.map((s) => s.label).join(',');
                    }
                }
                catch {
                    // No existing slices -- that's fine
                }
            }
            else if (libraryRoot && nodeType === 'program') {
                // Programs: load zone labels but no slice boundaries (zones don't
                // store startSample/endSample). The chopper shows the waveform
                // and the user re-slices from scratch.
                try {
                    const programMeta = await loadProgramMeta(libraryRoot, name);
                    initialLabels = programMeta.zones.map((z) => z.label ?? '').filter(Boolean).join(',');
                    chopperSampleName = programMeta.sourceInfo?.sampleName ?? name;
                }
                catch {
                    // Failed to load program metadata -- open chopper without labels
                }
            }
            setChopper({
                open: true, samples: wav.samples, sampleRate: wav.sampleRate,
                sampleName: chopperSampleName,
                origin: { name, type: nodeType, path },
                initialSlices, initialLabels,
            });
        }
        catch (err) {
            onError(err instanceof Error ? err.message : 'Failed to load sample');
        }
    }, [loadWavData, libraryRoot, onError]);
    const handleChopperSave = useCallback(async (payload) => {
        if (!libraryRoot || !chopper?.origin)
            return;
        const sampleFilename = 'sample.wav';
        // Build zones from slices -- all zones reference the same WAV file
        const zones = payload.slices.map((s, i) => {
            const zone = {
                sample: sampleFilename,
                label: s.label,
            };
            // Assign mute groups from playback config if present
            if (payload.playbackConfig?.muteGroups) {
                const muteGroupValue = payload.playbackConfig.muteGroups[i];
                if (muteGroupValue !== undefined && muteGroupValue !== 0) {
                    zone.muteGroup = muteGroupValue;
                }
            }
            return zone;
        });
        // Resolve source sample name: for programs, use the original source
        // sample name from the program's metadata; otherwise use the origin name.
        const sourceSampleName = chopper.origin.type === 'program'
            ? chopper.sampleName
            : chopper.origin.name;
        let program = {
            format: 'program',
            version: 1,
            name: payload.name,
            zones,
            polyphony: payload.playbackConfig?.polyphony,
            playbackMode: payload.playbackConfig?.playbackMode,
            sourceInfo: {
                sampleName: sourceSampleName,
            },
            modifiedAt: new Date().toISOString(),
        };
        // Let strategy add device-specific metadata (e.g., S3K drum kit key mappings)
        if (strategy.transformChopperProgram) {
            program = strategy.transformChopperProgram(program);
        }
        const wavData = createWav(payload.sourceAudio.samples, payload.sourceAudio.sampleRate);
        try {
            await saveProgram(libraryRoot, {
                name: payload.name,
                yaml: program,
                wavFiles: [{ filename: sampleFilename, data: wavData }],
            });
            onRefresh();
        }
        catch (err) {
            onError(err instanceof Error ? err.message : 'Failed to save chopped program');
        }
    }, [libraryRoot, chopper, strategy, onRefresh, onError]);
    // ---------------------------------------------------------------------------
    // Drum kit editor (metadata/pad editing)
    // ---------------------------------------------------------------------------
    const handleOpenDrumKitEditor = useCallback((name, nodeType, path) => {
        setDrumKitEditor({ open: true, kitName: name, kitPath: path ?? [], nodeType });
    }, []);
    // ---------------------------------------------------------------------------
    // Close handlers
    // ---------------------------------------------------------------------------
    const closeLoopEditor = useCallback(() => setLoopEditor(null), []);
    const closeSampleEditor = useCallback(() => setSampleEditor(null), []);
    const closeChopper = useCallback(() => setChopper(null), []);
    const closeSliceEditDialog = useCallback(() => setSliceEditDialog(null), []);
    const closeDrumKitEditor = useCallback(() => setDrumKitEditor(null), []);
    const createEditorActionHandler = useCallback(() => (actionId, name, nodeType, path) => {
        if (actionId === 'open-loop-editor')
            handleOpenInLoopEditor(name, nodeType, path);
        else if (actionId === 'open-chopper')
            handleOpenInChopper(name, nodeType, path);
        else if (actionId === 'open-sample-editor')
            handleOpenInSampleEditor(name, nodeType, path);
    }, [handleOpenInLoopEditor, handleOpenInChopper, handleOpenInSampleEditor]);
    return {
        loopEditor, sampleEditor, chopper, sliceEditDialog, drumKitEditor,
        handleOpenInLoopEditor, handleOpenInSampleEditor, handleOpenInChopper,
        handleOpenDrumKitEditor,
        createEditorActionHandler,
        handleLoopEditorSave, handleSampleEditorSave, handleChopperSave,
        setSliceEditDialog, setSampleEditor,
        closeLoopEditor, closeSampleEditor, closeChopper,
        closeSliceEditDialog, closeDrumKitEditor,
    };
}
//# sourceMappingURL=useEditorDialogsCore.js.map